interface SanitizedPasteResult {
  value: string;
  wordCount: number;
}

function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

function truncateToWordLimit(text: string, maxWords: number): string {
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(' ');
}

/**
 * Sanitizes pasted text (strips <> chars), inserts at cursor,
 * enforces word limit, and updates the textarea value + cursor position.
 * Returns the new value and word count so callers can sync their own state.
 */
export function sanitizePasteIntoTextarea(
  textarea: HTMLTextAreaElement,
  clipboardData: DataTransfer,
  maxWordCount: number,
): SanitizedPasteResult | null {
  const pasted = clipboardData.getData('text/plain').replace(/[<>]/g, '');
  if (!pasted) return null;

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const hasSelection = start !== end;

  // Already at limit with no selection — nothing to replace, block paste
  if (!hasSelection && countWords(textarea.value) >= maxWordCount) return null;

  const before = textarea.value.slice(0, start);
  const after = textarea.value.slice(end);
  const combined = before + pasted + after;

  const words = countWords(combined);
  const value = words > maxWordCount ? truncateToWordLimit(combined, maxWordCount) : combined;

  textarea.value = value;
  const cursorPos = Math.min(start + pasted.length, value.length);
  textarea.setSelectionRange(cursorPos, cursorPos);

  return { value, wordCount: countWords(value) };
}
